import { useMemo, useState } from "react";
import { ArrowUp, ArrowDown, ArrowSquareOut } from "@phosphor-icons/react";

const SORTS = [
  { key: "citations", label: "Citations" },
  { key: "year", label: "Year" },
  { key: "title", label: "Title" },
];

export const PublicationsTable = ({ publications, onSelect }) => {
  const [sortKey, setSortKey] = useState("citations");
  const [dir, setDir] = useState("desc");
  const [query, setQuery] = useState("");

  const sorted = useMemo(() => {
    const filtered = publications.filter((p) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return (
        (p.title || "").toLowerCase().includes(q) ||
        (p.authors || "").toLowerCase().includes(q) ||
        (p.venue || "").toLowerCase().includes(q)
      );
    });
    const cmp = (a, b) => {
      const A = a[sortKey] ?? (sortKey === "title" ? "" : 0);
      const B = b[sortKey] ?? (sortKey === "title" ? "" : 0);
      if (typeof A === "string") {
        return dir === "asc" ? A.localeCompare(B) : B.localeCompare(A);
      }
      return dir === "asc" ? (A || 0) - (B || 0) : (B || 0) - (A || 0);
    };
    return [...filtered].sort(cmp);
  }, [publications, sortKey, dir, query]);

  const toggleSort = (key) => {
    if (sortKey === key) setDir(dir === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setDir(key === "title" ? "asc" : "desc");
    }
  };

  return (
    <div className="editorial-card" data-testid="publications-table">
      <div className="p-5 border-b border-paper-edge flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-ink-mute">
          <span>Sort:</span>
          {SORTS.map((s) => {
            const active = sortKey === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => toggleSort(s.key)}
                data-testid={`sort-${s.key}`}
                className={`inline-flex items-center gap-1 px-2 py-1 border transition-colors ${
                  active
                    ? "border-ink bg-ink text-paper"
                    : "border-ink/15 hover:border-ink/40"
                }`}
              >
                {s.label}
                {active && (dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
              </button>
            );
          })}
        </div>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by title, author, venue…"
          className="bg-paper-alt border border-paper-edge px-3 py-2 text-sm font-mono w-full sm:w-72 focus:outline-none focus:border-ink/40"
          data-testid="publications-search"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="font-mono text-[10px] uppercase tracking-wider text-ink-mute">
              <th className="px-5 py-3 font-medium">Title & venue</th>
              <th className="px-5 py-3 font-medium hidden md:table-cell w-[18%]">Authors</th>
              <th className="px-5 py-3 font-medium text-right w-[8%]">Year</th>
              <th className="px-5 py-3 font-medium text-right w-[12%]">Cites</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p, idx) => (
              <tr
                key={`${p.title}-${idx}`}
                className={`border-t border-paper-edge transition-colors ${
                  onSelect && p.paper_id
                    ? "cursor-pointer hover:bg-saffron/8"
                    : "hover:bg-paper-alt/60"
                }`}
                onClick={() => {
                  if (onSelect && p.paper_id) onSelect(p);
                }}
                data-testid={`publication-row-${idx}`}
              >
                <td className="px-5 py-4 align-top">
                  <div
                    className="font-display text-lg leading-snug hover:text-terracotta transition-colors inline-flex items-start gap-2"
                  >
                    <span>{p.title}</span>
                    {p.link && (
                      <a
                        href={p.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="opacity-50 hover:opacity-100 mt-1.5"
                        data-testid={`publication-external-${idx}`}
                      >
                        <ArrowSquareOut size={13} weight="bold" />
                      </a>
                    )}
                  </div>
                  {p.venue && (
                    <div className="text-xs text-ink-mute italic mt-1 max-w-[60ch]">{p.venue}</div>
                  )}
                </td>
                <td className="px-5 py-4 align-top text-xs text-ink-mute hidden md:table-cell">
                  {p.authors}
                </td>
                <td className="px-5 py-4 align-top text-right font-mono text-sm">
                  {p.year ?? "—"}
                </td>
                <td className="px-5 py-4 align-top text-right">
                  <span
                    className={`font-mono text-sm ${
                      (p.citations || 0) > 0 ? "text-terracotta font-medium" : "text-ink-mute"
                    }`}
                  >
                    {p.citations || 0}
                  </span>
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={4} className="px-5 py-10 text-center text-ink-mute font-mono text-sm">
                  No publications match your filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PublicationsTable;
